import { call, put, takeEvery, takeLatest, delay, select, cancelled } from 'redux-saga/effects';
import { jwtDecode } from 'jwt-decode';
import { pb } from '../../../helpers/pocketbase_helper';
import {
  SHOW_EXPIRATION_WARNING,
  HIDE_EXPIRATION_WARNING,
  RENEW_TOKEN,
  RENEW_TOKEN_SUCCESS,
  RENEW_TOKEN_FAILURE,
  START_EXPIRATION_MONITORING,
  STOP_EXPIRATION_MONITORING,
  CHECK_TOKEN_EXPIRATION,
  DISMISS_WARNING
} from './actions';

// Constants
const EXPIRATION_WARNING_THRESHOLD = 5 * 60 * 1000; // 5 minutes in milliseconds
const MONITORING_INTERVAL = 60 * 1000; // Check every minute

/**
 * Checks if a token is approaching expiration (within 5 minutes)
 * @param {string} token - JWT token to check
 * @returns {Object|null} - Warning data if expiring soon, null otherwise
 */
function* checkTokenExpiration(token) {
  try {
    const decodedToken = jwtDecode(token);
    const currentTime = Date.now();
    const expirationTime = decodedToken.exp * 1000; // Convert to milliseconds
    const timeRemaining = expirationTime - currentTime;

    // Check if token is expiring within the warning threshold
    if (timeRemaining <= EXPIRATION_WARNING_THRESHOLD && timeRemaining > 0) {
      return {
        timeRemaining,
        expiresAt: expirationTime
      };
    }

    return null;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return null;
  }
}

/**
 * Checks token expiration from localStorage and shows warning if needed
 */
function* checkTokenExpirationSaga() {
  try {
    const authUserData = localStorage.getItem('authUser');
    
    if (!authUserData) {
      return;
    }

    const authUser = JSON.parse(authUserData);
    const token = authUser.token;

    if (!token) {
      return;
    }

    // Check if token is expiring soon
    const warningData = yield call(checkTokenExpiration, token);

    if (warningData) {
      yield put({
        type: SHOW_EXPIRATION_WARNING,
        payload: {
          timeRemaining: warningData.timeRemaining,
          expiresAt: warningData.expiresAt
        }
      });
    }

    // Update last checked time
    yield put({
      type: CHECK_TOKEN_EXPIRATION
    });

  } catch (error) {
    console.error('Error in checkTokenExpirationSaga:', error);
  }
}

/**
 * Handles token renewal requests
 */
function* handleTokenRenewalSaga() {
  try {
    yield put({ type: RENEW_TOKEN });

    // For now, we'll simulate a successful renewal
    // In a real implementation, this would call the backend to refresh the token
    const authUserData = localStorage.getItem('authUser');
    
    if (authUserData) {
      const authUser = JSON.parse(authUserData);
      
      // Simulate token refresh (in real implementation, call backend)
      const newExpiresAt = Date.now() + (60 * 60 * 1000); // 1 hour from now
      
      yield put({
        type: RENEW_TOKEN_SUCCESS,
        payload: {
          newToken: authUser.token, // In real implementation, this would be a new token
          newExpiresAt
        }
      });

      // Hide the warning
      yield put({ type: HIDE_EXPIRATION_WARNING });
    } else {
      throw new Error('No authentication data found');
    }

  } catch (error) {
    console.error('Token renewal failed:', error);
    yield put({
      type: RENEW_TOKEN_FAILURE,
      payload: { error: error.message }
    });
  }
}

/**
 * Handles user dismissal of expiration warning
 */
function* handleWarningDismissalSaga() {
  try {
    yield put({ type: HIDE_EXPIRATION_WARNING });
  } catch (error) {
    console.error('Error dismissing warning:', error);
  }
}

/**
 * Starts monitoring token expiration periodically
 */
function* startExpirationMonitoringSaga() {
  try {
    yield put({ type: START_EXPIRATION_MONITORING });

    while (true) {
      try {
        // Check token expiration
        yield call(checkTokenExpirationSaga);
        
        // Wait before next check
        yield delay(MONITORING_INTERVAL);
      } catch (error) {
        console.error('Error in monitoring loop:', error);
        yield delay(MONITORING_INTERVAL);
      }
    }
  } catch (error) {
    console.error('Error starting expiration monitoring:', error);
  } finally {
    if (yield cancelled()) {
      yield put({ type: STOP_EXPIRATION_MONITORING });
    }
  }
}

/**
 * Stops monitoring token expiration
 */
function* stopExpirationMonitoringSaga() {
  try {
    yield put({ type: STOP_EXPIRATION_MONITORING });
  } catch (error) {
    console.error('Error stopping expiration monitoring:', error);
  }
}

/**
 * Main token expiration saga watcher
 */
export function* tokenExpirationSaga() {
  yield takeEvery(CHECK_TOKEN_EXPIRATION, checkTokenExpirationSaga);
  yield takeEvery(RENEW_TOKEN, handleTokenRenewalSaga);
  yield takeEvery(DISMISS_WARNING, handleWarningDismissalSaga);
  yield takeLatest(START_EXPIRATION_MONITORING, startExpirationMonitoringSaga);
  yield takeEvery(STOP_EXPIRATION_MONITORING, stopExpirationMonitoringSaga);
}

// Export individual sagas for testing
export { 
  checkTokenExpirationSaga, 
  handleTokenRenewalSaga, 
  handleWarningDismissalSaga,
  startExpirationMonitoringSaga,
  stopExpirationMonitoringSaga
};
